"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** Fires once when the element is at least partly in view (or `play` is set). */
function useTrigger<T extends HTMLElement>(play: boolean | undefined) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (play !== undefined) return;
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [play]);
  return { ref, active: play ?? seen };
}

/**
 * Monospace label that "decodes": random letters settle into the real text
 * from left to right (Inversa-style). The real text is always in the DOM
 * for screen readers and search engines; only the visible copy animates.
 */
export function CcUsScramble({
  text,
  className,
  play,
}: {
  text: string;
  className?: string;
  /** Drive the effect from outside (e.g. when a chapter becomes active). */
  play?: boolean;
}) {
  const reduced = useCcPrefersReducedMotion();
  const { ref, active } = useTrigger<HTMLSpanElement>(play);
  const [shown, setShown] = useState(text);

  useEffect(() => {
    if (reduced || !active) return;
    let frame = 0;
    let tick = 0;
    const total = text.length;
    const run = () => {
      tick += 1;
      const settled = Math.floor(tick / 1.6);
      setShown(
        text
          .split("")
          .map((char, index) =>
            index < settled || char === " " || char === "·"
              ? char
              : GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
          )
          .join(""),
      );
      if (settled < total) frame = requestAnimationFrame(run);
    };
    frame = requestAnimationFrame(run);
    return () => cancelAnimationFrame(frame);
  }, [active, reduced, text]);

  return (
    <span ref={ref} className={className}>
      <span className="cc-us-sr">{text}</span>
      <span aria-hidden="true">{shown}</span>
    </span>
  );
}

/**
 * Body text that types itself out once in view. Renders the full text on
 * the server; on the client it hides the not-yet-typed part (kept in the
 * layout, so nothing jumps) and reveals it a few characters per frame.
 */
export function CcUsTypewriter({
  text,
  className,
  play,
  as: Tag = "p",
}: {
  text: string;
  className?: string;
  play?: boolean;
  as?: "p" | "span";
}) {
  const reduced = useCcPrefersReducedMotion();
  const { ref, active } = useTrigger<HTMLParagraphElement>(play);
  const [count, setCount] = useState(text.length);
  const armed = useRef(false);

  // Hide the text before first paint, unless motion is reduced.
  useIsoLayoutEffect(() => {
    if (reduced || armed.current) return;
    armed.current = true;
    setCount(0);
  }, [reduced]);

  useEffect(() => {
    if (reduced || !active) return;
    let frame = 0;
    let value = 0;
    const step = Math.max(1, Math.round(text.length / 70));
    const run = () => {
      value = Math.min(text.length, value + step);
      setCount(value);
      if (value < text.length) frame = requestAnimationFrame(run);
    };
    frame = requestAnimationFrame(run);
    return () => cancelAnimationFrame(frame);
  }, [active, reduced, text]);

  const visible = reduced ? text.length : count;

  return (
    <Tag ref={ref} className={className}>
      <span className="cc-us-sr">{text}</span>
      <span aria-hidden="true">
        {text.slice(0, visible)}
        <span className="cc-us-type__rest">{text.slice(visible)}</span>
      </span>
    </Tag>
  );
}
